import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { authClient } from '../lib/auth-client';

export default function LoginPage() {
  const navigate = useNavigate();
  const { data: session } = authClient.useSession();
  const [searchParams] = useSearchParams();
  const justReset = searchParams.get('reset') === '1';
  const justVerified = searchParams.get('verified') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session) navigate('/dashboard', { replace: true });
  }, [session, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResent(false);
    setLoading(true);

    try {
      const result = await authClient.signIn.email({ email, password });
      if (result.error) {
        const code = result.error.code;
        if (code === 'EMAIL_NOT_VERIFIED') {
          setUnverified(true);
        } else {
          setError(result.error.message || 'Sign in failed');
        }
        setLoading(false);
      }
      // On success, the session atom updates asynchronously; the effect above
      // handles navigation once ProtectedRoute will see a valid session.
    } catch {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/verify-email?status=success`,
      });
      setResent(true);
    } catch {
      setError('Could not resend verification email');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface-container-low)] px-6">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <h1 className="display-tight text-3xl font-semibold">Welcome Back</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Sign in to continue planning your day
          </p>
        </div>

        <div className="rounded-3xl bg-card p-8 shadow-ambient">
          <form onSubmit={handleSubmit} className="space-y-5">
            {(justReset || justVerified) && (
              <div className="rounded-2xl bg-emerald-500/10 p-3 text-sm text-emerald-700">
                {justReset
                  ? 'Password updated. Sign in with your new password.'
                  : 'Email verified. You can sign in now.'}
              </div>
            )}
            {error && (
              <div className="rounded-2xl bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            {unverified && (
              <div className="rounded-2xl bg-amber-500/10 p-3 text-sm text-amber-800 space-y-2">
                <p>Please verify your email before signing in.</p>
                {resent ? (
                  <p className="text-xs">Verification email resent. Check your inbox.</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-xs font-medium underline underline-offset-2"
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="label-caps text-xs text-muted-foreground">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus:bg-[var(--color-surface-bright)] focus:shadow-[0_4px_16px_rgba(49,51,47,0.06)] focus:outline-none"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label-caps text-xs text-muted-foreground">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  Forgot?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="flex h-10 w-full rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] px-4 py-2 text-sm transition-all placeholder:text-muted-foreground focus:bg-[var(--color-surface-bright)] focus:shadow-[0_4px_16px_rgba(49,51,47,0.06)] focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary-gradient inline-flex h-11 w-full items-center justify-center rounded-full px-6 text-sm font-medium text-white shadow-ambient transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/signup" className="font-medium text-secondary underline-offset-4 hover:underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
